import React, { useState, useEffect, useMemo } from 'react';
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  CardHeader,
  Spinner,
  Alert,
  Table,
  Badge,
  Button,
  ButtonGroup
} from 'reactstrap';
import { Line, Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';
import MetricCard from '../components/BiOnline/components/MetricCard';
import DateRangePicker from '../components/BiOnline/components/DateRangePicker';
import biOnlineAPI from '../components/BiOnline/utils/api';
import { formatCurrency, formatNumber, formatDate } from '../components/BiOnline/utils/formatters';

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

const BiOnline = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCumulative, setShowCumulative] = useState(false);
  const [showCumulativeOrders, setShowCumulativeOrders] = useState(false);
  const [dateRange, setDateRange] = useState({
    from: new Date(new Date().getTime() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    to: new Date().toISOString().split('T')[0]
  });

  const [dashboardData, setDashboardData] = useState(null);
  const [b2bAnalytics, setB2bAnalytics] = useState(null);
  const [transactionStatus, setTransactionStatus] = useState(null);
  const [regionData, setRegionData] = useState(null);
  const [valueStreamData, setValueStreamData] = useState(null);

  // Calculate if we should group by week (date range > 30 days)
  const shouldGroupByWeek = useMemo(() => {
    const from = new Date(dateRange.from);
    const to = new Date(dateRange.to);
    const diffDays = Math.ceil((to - from) / (1000 * 60 * 60 * 24));
    return diffDays > 30;
  }, [dateRange]);

  // Helper: Aggregate data by week
  const aggregateByWeek = (data, revenueFields = ['corporate_revenue', 'consumer_revenue'], orderFields = ['corporate_orders', 'consumer_orders']) => {
    if (!data || data.length === 0) return [];
    
    const weeks = {};
    data.forEach(item => {
      const date = new Date(item.period);
      const weekStart = new Date(date);
      weekStart.setDate(date.getDate() - date.getDay());
      const weekKey = weekStart.toISOString().split('T')[0];
      
      if (!weeks[weekKey]) {
        weeks[weekKey] = { period: weekKey };
        revenueFields.forEach(f => weeks[weekKey][f] = 0);
        orderFields.forEach(f => weeks[weekKey][f] = 0);
      }
      
      revenueFields.forEach(f => weeks[weekKey][f] += item[f] || 0);
      orderFields.forEach(f => weeks[weekKey][f] += item[f] || 0);
    });
    
    return Object.values(weeks).sort((a, b) => a.period.localeCompare(b.period));
  };

  // Helper: Aggregate transaction trend by week
  const aggregateTxTrendByWeek = (data) => {
    if (!data || data.length === 0) return [];
    
    const weeks = {};
    data.forEach(item => {
      const date = new Date(item.period);
      const weekStart = new Date(date);
      weekStart.setDate(date.getDate() - date.getDay());
      const weekKey = weekStart.toISOString().split('T')[0];
      
      if (!weeks[weekKey]) {
        weeks[weekKey] = { period: weekKey, settled_count: 0, declined_count: 0, error_count: 0 };
      }
      
      weeks[weekKey].settled_count += item.settled_count || 0;
      weeks[weekKey].declined_count += item.declined_count || 0;
      weeks[weekKey].error_count += item.error_count || 0;
    });
    
    return Object.values(weeks).sort((a, b) => a.period.localeCompare(b.period));
  };

  // Helper: Normalize region names (combine US, USA, United States / MX, Mexico)
  const normalizeRegionName = (name) => {
    if (!name) return 'Unknown';
    const upper = name.toUpperCase().trim();
    if (upper === 'US' || upper === 'USA' || upper === 'UNITED STATES' || upper === 'UNITED STATES OF AMERICA') {
      return 'USA';
    }
    if (upper === 'MX' || upper === 'MEXICO' || upper === 'MÉXICO') {
      return 'MX';
    }
    return name;
  };

  // Helper: Consolidate regions with same normalized name
  const consolidateRegions = (regions) => {
    if (!regions || regions.length === 0) return [];
    
    const consolidated = {};
    regions.forEach(item => {
      const normalizedName = normalizeRegionName(item.region);
      if (!consolidated[normalizedName]) {
        consolidated[normalizedName] = {
          region: normalizedName,
          order_count: 0,
          total_revenue: 0,
          successful_orders: 0,
          declined_orders: 0
        };
      }
      consolidated[normalizedName].order_count += item.order_count || 0;
      consolidated[normalizedName].total_revenue += item.total_revenue || 0;
      consolidated[normalizedName].successful_orders += item.successful_orders || 0;
      consolidated[normalizedName].declined_orders += item.declined_orders || 0;
    });
    
    return Object.values(consolidated).sort((a, b) => b.total_revenue - a.total_revenue);
  };

  // Helper: Extract color from product name (for belt colors, etc.)
  const getColorFromName = (name) => {
    if (!name) return null;
    const lowerName = name.toLowerCase();
    
    // Belt colors (Lean Six Sigma)
    if (lowerName.includes('black')) return 'rgba(33, 37, 41, 0.85)';
    if (lowerName.includes('green')) return 'rgba(40, 167, 69, 0.85)';
    if (lowerName.includes('yellow')) return 'rgba(255, 193, 7, 0.85)';
    if (lowerName.includes('white')) return 'rgba(173, 181, 189, 0.85)';
    if (lowerName.includes('orange')) return 'rgba(255, 159, 64, 0.85)';
    if (lowerName.includes('red')) return 'rgba(220, 53, 69, 0.85)';
    if (lowerName.includes('blue')) return 'rgba(54, 162, 235, 0.85)';
    if (lowerName.includes('purple')) return 'rgba(153, 102, 255, 0.85)';
    if (lowerName.includes('gold')) return 'rgba(255, 215, 0, 0.85)';
    if (lowerName.includes('silver')) return 'rgba(192, 192, 192, 0.85)';
    if (lowerName.includes('bronze')) return 'rgba(205, 127, 50, 0.85)';
    
    return null; // No color found
  };

  // Default colors for charts without color coding
  const defaultColors = [
    'rgba(75, 192, 192, 0.8)', 'rgba(54, 162, 235, 0.8)', 'rgba(255, 206, 86, 0.8)',
    'rgba(255, 99, 132, 0.8)', 'rgba(153, 102, 255, 0.8)', 'rgba(255, 159, 64, 0.8)',
    'rgba(199, 199, 199, 0.8)', 'rgba(83, 102, 255, 0.8)', 'rgba(255, 99, 255, 0.8)', 
    'rgba(99, 255, 132, 0.8)', 'rgba(255, 182, 193, 0.8)', 'rgba(144, 238, 144, 0.8)'
  ];

  useEffect(() => {
    fetchAllData();
  }, [dateRange]);

  const fetchAllData = async () => {
    setLoading(true);
    setError(null);

    try {
      const [dashboard, b2b, txStatus, regions, valueStream] = await Promise.allSettled([
        biOnlineAPI.getDashboard(dateRange.from, dateRange.to),
        biOnlineAPI.getB2BAnalytics(dateRange.from, dateRange.to, 'daily'),
        biOnlineAPI.getTransactionStatus(dateRange.from, dateRange.to, 'daily'),
        biOnlineAPI.getRegionAnalytics ? 
          biOnlineAPI.getRegionAnalytics(dateRange.from, dateRange.to) : 
          Promise.resolve(null),
        biOnlineAPI.getRevenueByValueStream ?
          biOnlineAPI.getRevenueByValueStream(dateRange.from, dateRange.to) :
          Promise.resolve(null)
      ]);

      if (dashboard.status === 'fulfilled') setDashboardData(dashboard.value);
      if (b2b.status === 'fulfilled') setB2bAnalytics(b2b.value);
      if (txStatus.status === 'fulfilled') setTransactionStatus(txStatus.value);
      if (regions.status === 'fulfilled' && regions.value) setRegionData(regions.value);
      if (valueStream.status === 'fulfilled' && valueStream.value) setValueStreamData(valueStream.value);

    } catch (err) {
      console.error('BI Online error:', err);
      setError('Error loading data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleDateRangeChange = (newRange) => {
    setDateRange(newRange);
    setShowCumulative(false); // Reset to daily view on date change
    setShowCumulativeOrders(false); // Reset orders to daily view on date change
  };

  // Helpers
  const getSummary = () => dashboardData?.summary || {};
  const getTxSummary = () => transactionStatus?.summary || {};
  const getSuccessRate = () => {
    const total = getTxSummary().total_transactions || 0;
    const settled = getTxSummary().settled_count || 0;
    return total === 0 ? 0 : ((settled / total) * 100).toFixed(1);
  };

  // Base chart options - NO GRID, NO DATA LABELS
  const baseOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      datalabels: { display: false }
    },
    scales: {
      x: { grid: { display: false }, border: { display: false } },
      y: { grid: { display: false }, border: { display: false }, beginAtZero: true }
    }
  };

  // ============ CHART CONFIGURATIONS ============

  // Helper: Calculate cumulative values
  const calculateCumulative = (data) => {
    let runningTotal = 0;
    let runningCorporate = 0;
    let runningConsumer = 0;
    
    return data.map(item => {
      runningTotal += item.total;
      runningCorporate += item.corporate;
      runningConsumer += item.consumer;
      return {
        ...item,
        total: runningTotal,
        corporate: runningCorporate,
        consumer: runningConsumer
      };
    });
  };

  // 1. OVERALL SALES TREND (with cumulative toggle)
  const getOverallSalesChart = () => {
    if (!b2bAnalytics?.b2b_vs_b2c || b2bAnalytics.b2b_vs_b2c.length === 0) return null;

    const rawData = b2bAnalytics.b2b_vs_b2c;
    const chartData = shouldGroupByWeek ? aggregateByWeek(rawData) : rawData;

    let salesData = chartData.map(item => ({
      period: item.period,
      total: (item.corporate_revenue || 0) + (item.consumer_revenue || 0),
      corporate: item.corporate_revenue || 0,
      consumer: item.consumer_revenue || 0
    }));

    // Apply cumulative if toggled
    if (showCumulative) {
      salesData = calculateCumulative(salesData);
    }

    return {
      data: {
        labels: salesData.map(item => formatDate(item.period)),
        datasets: [
          {
            label: showCumulative ? 'Cumulative Sales' : 'Total Sales',
            data: salesData.map(item => item.total),
            borderColor: 'rgb(75, 192, 192)',
            backgroundColor: 'rgba(75, 192, 192, 0.1)',
            tension: 0.4, fill: true, borderWidth: 3
          },
          {
            label: showCumulative ? 'Cumulative Corporate' : 'Corporate',
            data: salesData.map(item => item.corporate),
            borderColor: 'rgb(54, 162, 235)',
            backgroundColor: 'transparent',
            tension: 0.4, borderWidth: 2, borderDash: [5, 5]
          },
          {
            label: showCumulative ? 'Cumulative Consumer' : 'Consumer',
            data: salesData.map(item => item.consumer),
            borderColor: 'rgb(255, 159, 64)',
            backgroundColor: 'transparent',
            tension: 0.4, borderWidth: 2, borderDash: [5, 5]
          }
        ]
      },
      options: {
        ...baseOptions,
        plugins: {
          datalabels: { display: false },
          legend: { display: true, position: 'top' },
          tooltip: { callbacks: { label: (ctx) => `${ctx.dataset.label}: ${formatCurrency(ctx.raw)}` } }
        },
        scales: {
          x: { grid: { display: false }, border: { display: false } },
          y: { grid: { display: false }, border: { display: false }, beginAtZero: true,
            ticks: { callback: (v) => '$' + formatNumber(v, 0) }
          }
        }
      }
    };
  };

  // 2. DAILY/WEEKLY ORDER VOLUME (with cumulative toggle)
  const getOrderVolumeChart = () => {
    if (!b2bAnalytics?.b2b_vs_b2c || b2bAnalytics.b2b_vs_b2c.length === 0) return null;

    const rawData = b2bAnalytics.b2b_vs_b2c;
    const chartData = shouldGroupByWeek ? aggregateByWeek(rawData) : rawData;

    let orderData = chartData.map(item => ({
      period: item.period,
      corporate: item.corporate_orders || 0,
      consumer: item.consumer_orders || 0
    }));

    // Apply cumulative if toggled
    if (showCumulativeOrders) {
      let runningCorporate = 0;
      let runningConsumer = 0;
      orderData = orderData.map(item => {
        runningCorporate += item.corporate;
        runningConsumer += item.consumer;
        return {
          ...item,
          corporate: runningCorporate,
          consumer: runningConsumer
        };
      });
    }

    return {
      data: {
        labels: orderData.map(item => formatDate(item.period)),
        datasets: [
          {
            label: showCumulativeOrders ? 'Cumulative Corporate Orders' : 'Corporate Orders',
            data: orderData.map(item => item.corporate),
            borderColor: 'rgb(54, 162, 235)',
            backgroundColor: 'rgba(54, 162, 235, 0.1)',
            tension: 0.4, fill: true, borderWidth: 2
          },
          {
            label: showCumulativeOrders ? 'Cumulative Consumer Orders' : 'Consumer Orders',
            data: orderData.map(item => item.consumer),
            borderColor: 'rgb(255, 159, 64)',
            backgroundColor: 'rgba(255, 159, 64, 0.1)',
            tension: 0.4, fill: true, borderWidth: 2
          }
        ]
      },
      options: {
        ...baseOptions,
        plugins: {
          datalabels: { display: false },
          legend: { display: true, position: 'top' },
          tooltip: { callbacks: { label: (ctx) => `${ctx.dataset.label}: ${formatNumber(ctx.raw)}` } }
        }
      }
    };
  };

  // 3. AVERAGE ORDER VALUE TREND
  const getAOVTrendChart = () => {
    if (!b2bAnalytics?.b2b_vs_b2c || b2bAnalytics.b2b_vs_b2c.length === 0) return null;

    const rawData = b2bAnalytics.b2b_vs_b2c;
    const chartData = shouldGroupByWeek ? aggregateByWeek(rawData) : rawData;

    return {
      data: {
        labels: chartData.map(item => formatDate(item.period)),
        datasets: [{
          label: 'Avg Order Value',
          data: chartData.map(item => {
            const totalRev = (item.corporate_revenue || 0) + (item.consumer_revenue || 0);
            const totalOrders = (item.corporate_orders || 0) + (item.consumer_orders || 0);
            return totalOrders > 0 ? totalRev / totalOrders : 0;
          }),
          borderColor: 'rgb(153, 102, 255)',
          backgroundColor: 'rgba(153, 102, 255, 0.1)',
          tension: 0.4, fill: true, borderWidth: 2
        }]
      },
      options: {
        ...baseOptions,
        plugins: {
          datalabels: { display: false },
          legend: { display: false },
          tooltip: { callbacks: { label: (ctx) => `AOV: ${formatCurrency(ctx.raw)}` } }
        },
        scales: {
          x: { grid: { display: false }, border: { display: false } },
          y: { grid: { display: false }, border: { display: false }, beginAtZero: true,
            ticks: { callback: (v) => '$' + formatNumber(v, 0) }
          }
        }
      }
    };
  };

  // 4. REVENUE SPLIT - Horizontal Bar
  const getRevenueSplitChart = () => {
    const corporate = getSummary().corporate_revenue || 0;
    const consumer = getSummary().consumer_revenue || 0;
    if (corporate === 0 && consumer === 0) return null;

    return {
      data: {
        labels: ['Corporate (B2B)', 'Consumer (B2C)'],
        datasets: [{
          data: [corporate, consumer],
          backgroundColor: ['rgba(54, 162, 235, 0.8)', 'rgba(255, 159, 64, 0.8)'],
          borderColor: ['rgb(54, 162, 235)', 'rgb(255, 159, 64)'],
          borderWidth: 2, borderRadius: 4
        }]
      },
      options: {
        ...baseOptions,
        indexAxis: 'y',
        plugins: {
          datalabels: { display: false },
          legend: { display: false },
          tooltip: { callbacks: { label: (ctx) => {
            const total = corporate + consumer;
            const pct = ((ctx.raw / total) * 100).toFixed(1);
            return `${formatCurrency(ctx.raw)} (${pct}%)`;
          }}}
        },
        scales: {
          x: { grid: { display: false }, border: { display: false }, beginAtZero: true,
            ticks: { callback: (v) => '$' + formatNumber(v, 0) }
          },
          y: { grid: { display: false }, border: { display: false } }
        }
      }
    };
  };

  // 5. SALES BY REGION (with consolidated US/USA)
  const getSalesByRegionChart = () => {
    const regions = regionData?.regions || null;
    if (!regions || regions.length === 0) return null;

    // Consolidate regions (combine US, USA, United States)
    const consolidatedRegions = consolidateRegions(regions);

    return {
      data: {
        labels: consolidatedRegions.map(item => item.region),
        datasets: [{
          label: 'Revenue',
          data: consolidatedRegions.map(item => item.total_revenue || 0),
          backgroundColor: defaultColors.slice(0, consolidatedRegions.length),
          borderWidth: 0, borderRadius: 4
        }]
      },
      options: {
        ...baseOptions,
        indexAxis: 'y',
        plugins: {
          datalabels: { display: false },
          legend: { display: false },
          tooltip: { callbacks: { label: (ctx) => {
            const item = consolidatedRegions[ctx.dataIndex];
            return [`Revenue: ${formatCurrency(ctx.raw)}`, `Orders: ${formatNumber(item.order_count || 0)}`];
          }}}
        },
        scales: {
          x: { grid: { display: false }, border: { display: false }, beginAtZero: true,
            ticks: { callback: (v) => '$' + formatNumber(v, 0) }
          },
          y: { grid: { display: false }, border: { display: false } }
        }
      }
    };
  };

  // 6. TOP STATES
  const getTopStatesChart = () => {
    const states = regionData?.top_states || null;
    if (!states || states.length === 0) return null;

    return {
      data: {
        labels: states.slice(0, 10).map(item => item.state || 'Unknown'),
        datasets: [{
          label: 'Revenue',
          data: states.slice(0, 10).map(item => item.total_revenue || 0),
          backgroundColor: defaultColors.slice(0, 10),
          borderWidth: 0, borderRadius: 4
        }]
      },
      options: {
        ...baseOptions,
        indexAxis: 'y',
        plugins: {
          datalabels: { display: false },
          legend: { display: false },
          tooltip: { callbacks: { label: (ctx) => formatCurrency(ctx.raw) } }
        },
        scales: {
          x: { grid: { display: false }, border: { display: false }, beginAtZero: true,
            ticks: { callback: (v) => '$' + formatNumber(v, 0) }
          },
          y: { grid: { display: false }, border: { display: false } }
        }
      }
    };
  };

  // 7. VALUE STREAM REVENUE
  const getValueStreamChart = () => {
    if (!valueStreamData || valueStreamData.length === 0) return null;

    return {
      data: {
        labels: valueStreamData.map(item => item.value_stream || 'Unassigned'),
        datasets: [{
          label: 'Revenue',
          data: valueStreamData.map(item => item.revenue || 0),
          backgroundColor: defaultColors.slice(0, valueStreamData.length),
          borderWidth: 0, borderRadius: 4
        }]
      },
      options: {
        ...baseOptions,
        indexAxis: 'y',
        plugins: {
          datalabels: { display: false },
          legend: { display: false },
          tooltip: { callbacks: { label: (ctx) => {
            const item = valueStreamData[ctx.dataIndex];
            return [`Revenue: ${formatCurrency(ctx.raw)}`, `Orders: ${formatNumber(item.orders || 0)}`];
          }}}
        },
        scales: {
          x: { grid: { display: false }, border: { display: false }, beginAtZero: true,
            ticks: { callback: (v) => '$' + formatNumber(v, 0) }
          },
          y: { grid: { display: false }, border: { display: false } }
        }
      }
    };
  };

  // 8. TOP PROYECTOS (with color coding based on product name)
  const getTopProyectosChart = () => {
    const proyectos = dashboardData?.top_proyectos || null;
    if (!proyectos || proyectos.length === 0) return null;

    // Generate colors based on product names, fallback to default colors
    const colors = proyectos.map((item, index) => {
      const nameColor = getColorFromName(item.proyecto_name);
      return nameColor || defaultColors[index % defaultColors.length];
    });

    return {
      data: {
        labels: proyectos.map(item => item.proyecto_name || item.proyecto_code || 'Unknown'),
        datasets: [{
          label: 'Revenue',
          data: proyectos.map(item => item.revenue || 0),
          backgroundColor: colors,
          borderWidth: 0, borderRadius: 4
        }]
      },
      options: {
        ...baseOptions,
        indexAxis: 'y',
        plugins: {
          datalabels: { display: false },
          legend: { display: false },
          tooltip: { callbacks: { label: (ctx) => {
            const item = proyectos[ctx.dataIndex];
            return [`Revenue: ${formatCurrency(ctx.raw)}`, `Orders: ${formatNumber(item.orders || 0)}`];
          }}}
        },
        scales: {
          x: { grid: { display: false }, border: { display: false }, beginAtZero: true,
            ticks: { callback: (v) => '$' + formatNumber(v, 0) }
          },
          y: { grid: { display: false }, border: { display: false } }
        }
      }
    };
  };

  // 8b. REVENUE BY PROJECT CODE
  const getRevenueByProjectCodeChart = () => {
    const proyectos = dashboardData?.top_proyectos || null;
    if (!proyectos || proyectos.length === 0) return null;

    // Generate colors based on product names
    const colors = proyectos.map((item, index) => {
      const nameColor = getColorFromName(item.proyecto_name);
      return nameColor || defaultColors[index % defaultColors.length];
    });

    return {
      data: {
        labels: proyectos.map(item => item.proyecto_code || 'Unknown'),
        datasets: [{
          label: 'Revenue',
          data: proyectos.map(item => item.revenue || 0),
          backgroundColor: colors,
          borderWidth: 0, borderRadius: 4
        }]
      },
      options: {
        ...baseOptions,
        indexAxis: 'y',
        plugins: {
          datalabels: { display: false },
          legend: { display: false },
          tooltip: { callbacks: { label: (ctx) => {
            const item = proyectos[ctx.dataIndex];
            return [
              `Product: ${item.proyecto_name || 'N/A'}`,
              `Revenue: ${formatCurrency(ctx.raw)}`, 
              `Orders: ${formatNumber(item.orders || 0)}`
            ];
          }}}
        },
        scales: {
          x: { grid: { display: false }, border: { display: false }, beginAtZero: true,
            ticks: { callback: (v) => '$' + formatNumber(v, 0) }
          },
          y: { grid: { display: false }, border: { display: false } }
        }
      }
    };
  };

  // 9. NEW COMPANIES TREND
  const getNewCompaniesChart = () => {
    if (!b2bAnalytics?.new_companies_trend || b2bAnalytics.new_companies_trend.length === 0) return null;

    // Aggregate by week if needed
    let chartData = b2bAnalytics.new_companies_trend;
    if (shouldGroupByWeek) {
      const weeks = {};
      chartData.forEach(item => {
        const date = new Date(item.period);
        const weekStart = new Date(date);
        weekStart.setDate(date.getDate() - date.getDay());
        const weekKey = weekStart.toISOString().split('T')[0];
        
        if (!weeks[weekKey]) {
          weeks[weekKey] = { period: weekKey, new_companies: 0, company_domains: [] };
        }
        weeks[weekKey].new_companies += item.new_companies || 0;
        if (item.company_domains) {
          weeks[weekKey].company_domains.push(...item.company_domains);
        }
      });
      chartData = Object.values(weeks).sort((a, b) => a.period.localeCompare(b.period));
    }

    return {
      data: {
        labels: chartData.map(item => formatDate(item.period)),
        datasets: [{
          label: 'New Companies',
          data: chartData.map(item => item.new_companies || 0),
          borderColor: 'rgb(153, 102, 255)',
          backgroundColor: 'rgba(153, 102, 255, 0.2)',
          tension: 0.4, fill: true
        }]
      },
      options: {
        ...baseOptions,
        plugins: {
          datalabels: { display: false },
          legend: { display: false },
          tooltip: { callbacks: { afterLabel: (ctx) => {
            const item = chartData[ctx.dataIndex];
            if (item?.company_domains?.length > 0) {
              return item.company_domains.slice(0, 3).join(', ') + (item.company_domains.length > 3 ? '...' : '');
            }
            return '';
          }}}
        },
        scales: {
          x: { grid: { display: false }, border: { display: false } },
          y: { grid: { display: false }, border: { display: false }, beginAtZero: true, ticks: { stepSize: 1 } }
        }
      }
    };
  };

  // 10. B2B vs B2C WEEKLY STACKED
  const getB2BvsB2CChart = () => {
    if (!b2bAnalytics?.b2b_vs_b2c || b2bAnalytics.b2b_vs_b2c.length === 0) return null;

    // Always aggregate by week for this chart
    const weeklyData = aggregateByWeek(b2bAnalytics.b2b_vs_b2c);

    return {
      data: {
        labels: weeklyData.map(item => formatDate(item.period)),
        datasets: [
          { label: 'Corporate (B2B)', data: weeklyData.map(item => item.corporate_revenue),
            backgroundColor: 'rgba(54, 162, 235, 0.8)', borderColor: 'rgb(54, 162, 235)', borderWidth: 1 },
          { label: 'Consumer (B2C)', data: weeklyData.map(item => item.consumer_revenue),
            backgroundColor: 'rgba(255, 159, 64, 0.8)', borderColor: 'rgb(255, 159, 64)', borderWidth: 1 }
        ]
      },
      options: {
        ...baseOptions,
        plugins: {
          datalabels: { display: false },
          legend: { display: true, position: 'top' },
          tooltip: { callbacks: { label: (ctx) => `${ctx.dataset.label}: ${formatCurrency(ctx.raw)}` } }
        },
        scales: {
          x: { stacked: true, grid: { display: false }, border: { display: false } },
          y: { stacked: true, grid: { display: false }, border: { display: false }, beginAtZero: true,
            ticks: { callback: (v) => '$' + formatNumber(v, 0) }
          }
        }
      }
    };
  };

  // 11. TRANSACTION STATUS OVER TIME
  const getTransactionStatusChart = () => {
    if (!transactionStatus?.trend || transactionStatus.trend.length === 0) return null;

    const rawData = transactionStatus.trend;
    const chartData = shouldGroupByWeek ? aggregateTxTrendByWeek(rawData) : rawData;

    return {
      data: {
        labels: chartData.map(item => formatDate(item.period)),
        datasets: [
          { label: 'Settled', data: chartData.map(item => item.settled_count || 0),
            borderColor: 'rgb(40, 167, 69)', backgroundColor: 'rgba(40, 167, 69, 0.1)', tension: 0.4, fill: true },
          { label: 'Declined', data: chartData.map(item => item.declined_count || 0),
            borderColor: 'rgb(255, 193, 7)', backgroundColor: 'rgba(255, 193, 7, 0.1)', tension: 0.4, fill: true },
          { label: 'Errors', data: chartData.map(item => item.error_count || 0),
            borderColor: 'rgb(220, 53, 69)', backgroundColor: 'rgba(220, 53, 69, 0.1)', tension: 0.4, fill: true }
        ]
      },
      options: {
        ...baseOptions,
        plugins: { 
          datalabels: { display: false },
          legend: { display: true, position: 'top' } 
        }
      }
    };
  };

  // 12. TRANSACTION BREAKDOWN - Horizontal Bar
  const getTransactionStatusBar = () => {
    const settled = getTxSummary().settled_count || 0;
    const declined = getTxSummary().declined_count || 0;
    const errors = getTxSummary().error_count || 0;
    if (settled === 0 && declined === 0 && errors === 0) return null;

    return {
      data: {
        labels: ['Settled', 'Declined', 'Errors'],
        datasets: [{
          data: [settled, declined, errors],
          backgroundColor: ['rgba(40, 167, 69, 0.8)', 'rgba(255, 193, 7, 0.8)', 'rgba(220, 53, 69, 0.8)'],
          borderWidth: 0, borderRadius: 4
        }]
      },
      options: {
        ...baseOptions,
        indexAxis: 'y',
        plugins: {
          datalabels: { display: false },
          legend: { display: false },
          tooltip: { callbacks: { label: (ctx) => {
            const total = settled + declined + errors;
            const pct = ((ctx.raw / total) * 100).toFixed(1);
            return `${formatNumber(ctx.raw)} (${pct}%)`;
          }}}
        }
      }
    };
  };

  // 13. PAYMENT METHODS
  const getPaymentMethodChart = () => {
    if (!transactionStatus?.payment_method_analysis || transactionStatus.payment_method_analysis.length === 0) return null;

    return {
      data: {
        labels: transactionStatus.payment_method_analysis.map(item => item.payment_method || 'Unknown'),
        datasets: [{
          label: 'Transactions',
          data: transactionStatus.payment_method_analysis.map(item => item.transaction_count || item.total_attempts || 0),
          backgroundColor: defaultColors.slice(0, transactionStatus.payment_method_analysis.length),
          borderWidth: 0, borderRadius: 4
        }]
      },
      options: {
        ...baseOptions,
        indexAxis: 'y',
        plugins: {
          datalabels: { display: false },
          legend: { display: false },
          tooltip: { callbacks: { label: (ctx) => {
            const item = transactionStatus.payment_method_analysis[ctx.dataIndex];
            return [`Transactions: ${formatNumber(ctx.raw)}`, `Revenue: ${formatCurrency(item.revenue || 0)}`];
          }}}
        }
      }
    };
  };

  return (
    <Container fluid className="bi-online-page">
      <Row className="mb-4">
        <Col>
          <h1 className="page-title">BI Online Dashboard</h1>
          <p className="text-muted">
            Online sales analytics and B2B performance metrics
            {shouldGroupByWeek && <Badge color="info" className="ms-2">Weekly View</Badge>}
          </p>
        </Col>
      </Row>

      {/* Date Range Picker */}
      <Row className="mb-4">
        <Col>
          <Card>
            <CardBody>
              <DateRangePicker onRangeChange={handleDateRangeChange} defaultRange="Last 30 Days" />
            </CardBody>
          </Card>
        </Col>
      </Row>

      {error && <Row className="mb-4"><Col><Alert color="danger">{error}</Alert></Col></Row>}

      {loading ? (
        <Row className="mb-4">
          <Col className="text-center">
            <Spinner color="primary" style={{ width: '3rem', height: '3rem' }} />
            <p className="mt-3">Loading data...</p>
          </Col>
        </Row>
      ) : (
        <>
          {/* KPI Cards Row 1 - Revenue */}
          {dashboardData && (
            <Row className="mb-4">
              <Col lg="3" md="6" className="mb-3">
                <MetricCard title="Total Revenue" value={getSummary().total_revenue || 0} type="currency" color="success"
                  subtitle={`${formatNumber(getSummary().total_orders || 0)} orders`} />
              </Col>
              <Col lg="3" md="6" className="mb-3">
                <MetricCard title="Corporate Revenue" value={getSummary().corporate_revenue || 0} type="currency" color="primary"
                  subtitle={`${formatNumber(getSummary().corporate_orders || 0)} corporate orders`} />
              </Col>
              <Col lg="3" md="6" className="mb-3">
                <MetricCard title="Consumer Revenue" value={getSummary().consumer_revenue || 0} type="currency" color="info"
                  subtitle={`${formatNumber(getSummary().consumer_orders || 0)} consumer orders`} />
              </Col>
              <Col lg="3" md="6" className="mb-3">
                <MetricCard title="Avg Order Value" value={getSummary().avg_order_value || 0} type="currency" color="warning" />
              </Col>
            </Row>
          )}

          {/* KPI Cards Row 2 - Transactions */}
          {transactionStatus && (
            <Row className="mb-4">
              <Col lg="3" md="6" className="mb-3">
                <MetricCard title="Total Transactions" value={getTxSummary().total_transactions || 0} type="number" color="primary"
                  subtitle={`${getSuccessRate()}% success rate`} />
              </Col>
              <Col lg="3" md="6" className="mb-3">
                <MetricCard title="Settled" value={getTxSummary().settled_count || 0} type="number" color="success"
                  subtitle={formatCurrency(getTxSummary().settled_revenue || 0)} />
              </Col>
              <Col lg="3" md="6" className="mb-3">
                <MetricCard title="Declined" value={getTxSummary().declined_count || 0} type="number" color="warning"
                  subtitle={formatCurrency(getTxSummary().declined_amount || 0)} />
              </Col>
              <Col lg="3" md="6" className="mb-3">
                <MetricCard title="Unique Companies" value={getSummary().unique_companies || 0} type="number" color="info"
                  subtitle={`${formatNumber(getSummary().total_customers || 0)} total customers`} />
              </Col>
            </Row>
          )}

          {/* Overall Sales Trend - Full Width */}
          <Row className="mb-4">
            <Col>
              <Card>
                <CardHeader className="d-flex justify-content-between align-items-center">
                  <div>
                    <h5 className="mb-0">
                      📈 {showCumulative ? 'Cumulative Sales' : 'Overall Sales Trend'} {shouldGroupByWeek ? '(Weekly)' : '(Daily)'}
                    </h5>
                  </div>
                  <ButtonGroup size="sm">
                    <Button 
                      color={!showCumulative ? 'primary' : 'secondary'} 
                      outline={showCumulative}
                      onClick={() => setShowCumulative(false)}
                    >
                      Daily
                    </Button>
                    <Button 
                      color={showCumulative ? 'primary' : 'secondary'} 
                      outline={!showCumulative}
                      onClick={() => setShowCumulative(true)}
                    >
                      Cumulative
                    </Button>
                  </ButtonGroup>
                </CardHeader>
                <CardBody style={{ height: '400px' }}>
                  {getOverallSalesChart() ? <Line data={getOverallSalesChart().data} options={getOverallSalesChart().options} /> 
                    : <div className="text-center text-muted py-5">No data available</div>}
                </CardBody>
              </Card>
            </Col>
          </Row>

          {/* Order Volume + AOV Trend */}
          <Row className="mb-4">
            <Col lg="8" className="mb-3">
              <Card className="h-100">
                <CardHeader className="d-flex justify-content-between align-items-center">
                  <div>
                    <h5 className="mb-0">
                      📦 {showCumulativeOrders ? 'Cumulative' : shouldGroupByWeek ? 'Weekly' : 'Daily'} Order Volume
                    </h5>
                  </div>
                  <ButtonGroup size="sm">
                    <Button 
                      color={!showCumulativeOrders ? 'primary' : 'secondary'} 
                      outline={showCumulativeOrders}
                      onClick={() => setShowCumulativeOrders(false)}
                    >
                      {shouldGroupByWeek ? 'Weekly' : 'Daily'}
                    </Button>
                    <Button 
                      color={showCumulativeOrders ? 'primary' : 'secondary'} 
                      outline={!showCumulativeOrders}
                      onClick={() => setShowCumulativeOrders(true)}
                    >
                      Cumulative
                    </Button>
                  </ButtonGroup>
                </CardHeader>
                <CardBody style={{ height: '300px' }}>
                  {getOrderVolumeChart() ? <Line data={getOrderVolumeChart().data} options={getOrderVolumeChart().options} />
                    : <div className="text-center text-muted py-5">No data available</div>}
                </CardBody>
              </Card>
            </Col>
            <Col lg="4" className="mb-3">
              <Card className="h-100">
                <CardHeader><h5 className="mb-0">💰 Avg Order Value Trend</h5></CardHeader>
                <CardBody style={{ height: '300px' }}>
                  {getAOVTrendChart() ? <Line data={getAOVTrendChart().data} options={getAOVTrendChart().options} />
                    : <div className="text-center text-muted py-5">No data available</div>}
                </CardBody>
              </Card>
            </Col>
          </Row>

          {/* B2B vs B2C + Revenue Split */}
          <Row className="mb-4">
            <Col lg="8" className="mb-3">
              <Card className="h-100">
                <CardHeader><h5 className="mb-0">📊 B2B vs B2C Revenue (Weekly)</h5></CardHeader>
                <CardBody style={{ height: '350px' }}>
                  {getB2BvsB2CChart() ? <Bar data={getB2BvsB2CChart().data} options={getB2BvsB2CChart().options} />
                    : <div className="text-center text-muted py-5">No data available</div>}
                </CardBody>
              </Card>
            </Col>
            <Col lg="4" className="mb-3">
              <Card className="h-100">
                <CardHeader><h5 className="mb-0">Revenue Split</h5></CardHeader>
                <CardBody style={{ height: '350px' }}>
                  {getRevenueSplitChart() ? <Bar data={getRevenueSplitChart().data} options={getRevenueSplitChart().options} />
                    : <div className="text-center text-muted py-5">No data available</div>}
                </CardBody>
              </Card>
            </Col>
          </Row>

          {/* Region + States */}
          <Row className="mb-4">
            <Col lg="6" className="mb-3">
              <Card className="h-100">
                <CardHeader><h5 className="mb-0">🌎 Sales by Country</h5></CardHeader>
                <CardBody style={{ height: '350px' }}>
                  {getSalesByRegionChart() ? <Bar data={getSalesByRegionChart().data} options={getSalesByRegionChart().options} />
                    : <div className="text-center text-muted py-5">No data available</div>}
                </CardBody>
              </Card>
            </Col>
            <Col lg="6" className="mb-3">
              <Card className="h-100">
                <CardHeader><h5 className="mb-0">🏙️ Top States</h5></CardHeader>
                <CardBody style={{ height: '350px' }}>
                  {getTopStatesChart() ? <Bar data={getTopStatesChart().data} options={getTopStatesChart().options} />
                    : <div className="text-center text-muted py-5">No data available</div>}
                </CardBody>
              </Card>
            </Col>
          </Row>

          {/* Value Stream + Proyectos */}
          <Row className="mb-4">
            <Col lg="6" className="mb-3">
              <Card className="h-100">
                <CardHeader><h5 className="mb-0">🏢 Revenue by Value Stream</h5></CardHeader>
                <CardBody style={{ height: '350px' }}>
                  {getValueStreamChart() ? <Bar data={getValueStreamChart().data} options={getValueStreamChart().options} />
                    : <div className="text-center text-muted py-5">No data available</div>}
                </CardBody>
              </Card>
            </Col>
            <Col lg="6" className="mb-3">
              <Card className="h-100">
                <CardHeader><h5 className="mb-0">📚 Top Products (Proyectos)</h5></CardHeader>
                <CardBody style={{ height: '350px' }}>
                  {getTopProyectosChart() ? <Bar data={getTopProyectosChart().data} options={getTopProyectosChart().options} />
                    : <div className="text-center text-muted py-5">No data available</div>}
                </CardBody>
              </Card>
            </Col>
          </Row>

          {/* Revenue by Project Code */}
          <Row className="mb-4">
            <Col>
              <Card>
                <CardHeader><h5 className="mb-0">🔢 Revenue by Project Code</h5></CardHeader>
                <CardBody style={{ height: '400px' }}>
                  {getRevenueByProjectCodeChart() ? <Bar data={getRevenueByProjectCodeChart().data} options={getRevenueByProjectCodeChart().options} />
                    : <div className="text-center text-muted py-5">No data available</div>}
                </CardBody>
              </Card>
            </Col>
          </Row>

          {/* New Companies + Payment Methods */}
          <Row className="mb-4">
            <Col lg="6" className="mb-3">
              <Card className="h-100">
                <CardHeader>
                  <h5 className="mb-0">🆕 New Companies Acquired {shouldGroupByWeek ? '(Weekly)' : ''}</h5>
                </CardHeader>
                <CardBody style={{ height: '300px' }}>
                  {getNewCompaniesChart() ? <Line data={getNewCompaniesChart().data} options={getNewCompaniesChart().options} />
                    : <div className="text-center text-muted py-5">No data available</div>}
                </CardBody>
              </Card>
            </Col>
            <Col lg="6" className="mb-3">
              <Card className="h-100">
                <CardHeader><h5 className="mb-0">💳 Payment Methods</h5></CardHeader>
                <CardBody style={{ height: '300px' }}>
                  {getPaymentMethodChart() ? <Bar data={getPaymentMethodChart().data} options={getPaymentMethodChart().options} />
                    : <div className="text-center text-muted py-5">No data available</div>}
                </CardBody>
              </Card>
            </Col>
          </Row>

          {/* Top Companies Table */}
          <Row className="mb-4">
            <Col>
              <Card>
                <CardHeader><h5 className="mb-0">🏆 Top B2B Companies</h5></CardHeader>
                <CardBody>
                  {dashboardData?.top_companies?.length > 0 ? (
                    <Table responsive striped hover>
                      <thead>
                        <tr>
                          <th>#</th>
                          <th>Company</th>
                          <th className="text-right">Total Revenue</th>
                          <th className="text-right">Orders</th>
                          <th className="text-right">Avg Order</th>
                        </tr>
                      </thead>
                      <tbody>
                        {dashboardData.top_companies.map((company, index) => (
                          <tr key={company.email_domain || index}>
                            <td><Badge color={index < 3 ? 'success' : 'secondary'}>{index + 1}</Badge></td>
                            <td>
                              <strong>{company.company_name || 'N/A'}</strong>
                              {company.email_domain && <code className="small d-block text-muted">{company.email_domain}</code>}
                            </td>
                            <td className="text-right text-success font-weight-bold">{formatCurrency(company.revenue || 0)}</td>
                            <td className="text-right">{formatNumber(company.orders || 0)}</td>
                            <td className="text-right">{formatCurrency(company.avg_order || 0)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  ) : <div className="text-center text-muted py-4">No companies found</div>}
                </CardBody>
              </Card>
            </Col>
          </Row>

          {/* Transaction Status + Breakdown - AT BOTTOM */}
          <Row className="mb-4">
            <Col lg="8" className="mb-3">
              <Card className="h-100">
                <CardHeader>
                  <h5 className="mb-0">📉 Transaction Status Over Time {shouldGroupByWeek ? '(Weekly)' : '(Daily)'}</h5>
                </CardHeader>
                <CardBody style={{ height: '300px' }}>
                  {getTransactionStatusChart() ? <Line data={getTransactionStatusChart().data} options={getTransactionStatusChart().options} />
                    : <div className="text-center text-muted py-5">No data available</div>}
                </CardBody>
              </Card>
            </Col>
            <Col lg="4" className="mb-3">
              <Card className="h-100">
                <CardHeader><h5 className="mb-0">Transaction Breakdown</h5></CardHeader>
                <CardBody style={{ height: '300px' }}>
                  {getTransactionStatusBar() ? <Bar data={getTransactionStatusBar().data} options={getTransactionStatusBar().options} />
                    : <div className="text-center text-muted py-5">No data available</div>}
                </CardBody>
              </Card>
            </Col>
          </Row>

          {/* Failure Analysis Table - AT BOTTOM */}
          <Row className="mb-4">
            <Col>
              <Card>
                <CardHeader>
                  <h5 className="mb-0">⚠️ Failure Analysis</h5>
                  <p className="text-muted small mb-0">Top reasons for declined transactions</p>
                </CardHeader>
                <CardBody>
                  {transactionStatus?.failure_analysis?.length > 0 ? (
                    <Table responsive size="sm" className="mb-0">
                      <thead>
                        <tr>
                          <th>Reason</th>
                          <th className="text-right">Count</th>
                          <th className="text-right">Amount</th>
                          <th className="text-right">% of Failures</th>
                        </tr>
                      </thead>
                      <tbody>
                        {transactionStatus.failure_analysis.slice(0, 10).map((item, index) => {
                          const totalFailed = getTxSummary().declined_count || 1;
                          const pct = ((item.failure_count || 0) / totalFailed * 100).toFixed(1);
                          return (
                            <tr key={index}>
                              <td>
                                <Badge color={index < 3 ? 'danger' : 'secondary'} className="me-2">{index + 1}</Badge>
                                {item.card_type || 'Unknown'}
                                {item.bill_to_country && <small className="text-muted ms-1">({item.bill_to_country})</small>}
                              </td>
                              <td className="text-right">{formatNumber(item.failure_count || 0)}</td>
                              <td className="text-right text-danger">{formatCurrency(item.lost_revenue || 0)}</td>
                              <td className="text-right">
                                <Badge color={pct > 20 ? 'danger' : pct > 10 ? 'warning' : 'success'}>{pct}%</Badge>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </Table>
                  ) : <div className="text-center text-muted py-4">No failure data available</div>}
                </CardBody>
              </Card>
            </Col>
          </Row>
        </>
      )}
    </Container>
  );
};

export default BiOnline;